/**
 * Shared attribution-channel presentation + the ONE client-side definition of
 * "attributable". Before this module, CHANNEL_LABELS/humanizeChannel/the money
 * formatter were pasted into three components and the "(direct)-only is an
 * empty report wearing a chart" rule existed in three variants — the Home
 * banner's extra revenue>0 clause meant an account could get the aha card AND
 * the email yet never the banner (simplify review, 2026-07-20).
 *
 * The email renderer keeps its own private copies deliberately: it must stay
 * import-free so its Deno twin stays byte-parity-tested (see
 * src/lib/emails/attribution-report-email.ts header).
 */

export const CHANNEL_LABELS: Record<string, string> = {
  direct: 'Direct',
  '(direct)': 'Direct',
  '(not set)': 'Unknown',
  '(none)': 'Unknown',
  unknown: 'Unknown',
};

const UNTRACKED_CHANNELS = new Set(['', 'direct', '(direct)', '(none)', '(not set)', 'unknown']);

/** A source bucket that means attribution was not observed, not a real channel. */
export function isUntrackedChannel(channel: string | null | undefined): boolean {
  return UNTRACKED_CHANNELS.has(channel?.trim().toLowerCase() ?? '');
}

export function humanizeChannel(channel: string): string {
  const mapped = CHANNEL_LABELS[channel];
  if (mapped) return mapped;
  if (!channel) return 'Unknown';
  return channel.charAt(0).toUpperCase() + channel.slice(1);
}

/** Cents → compact dollars. Honest-NULL: null/non-finite renders an em-dash. */
export function formatRevenueCents(cents: number | null | undefined, currency = 'USD'): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  const dollars = cents / 100;
  const symbol =
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 0,
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value ?? currency;
  if (Math.abs(dollars) >= 1_000_000) return `${symbol}${(dollars / 1_000_000).toFixed(1)}M`;
  if (Math.abs(dollars) >= 1000) return `${symbol}${(dollars / 1000).toFixed(1)}K`;
  return `${symbol}${Math.round(dollars)}`;
}

/**
 * Cents → dollars at FULL precision (`$17,998.80`), for a figure a merchant
 * reconciles against their own store rather than glances at.
 *
 * Deliberately a sibling of `formatRevenueCents` and not a replacement for it:
 * a ranked bar's axis and a channel row are glance surfaces where `$18.0K`
 * reads faster, but a product's revenue is a number someone checks against
 * Shopify. Rounding it there is the same class of quiet lie as compacting a
 * spend figure someone reconciles against an invoice.
 *
 * Honest-NULL, same contract as its sibling: null/non-finite renders an
 * em-dash, never `$0`.
 */
export function formatRevenueCentsExact(
  cents: number | null | undefined,
  currency = 'USD',
): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * The single client-side attributability rule: at least one channel that isn't
 * the "(direct)" bucket. Revenue may be honest-NULL — an attributed order whose
 * revenue hasn't synced is still attribution. Mirrors the server rule
 * (attribution-report-data.ts + the enqueue trigger's first_source predicate);
 * change all of them together or the surfaces will disagree again.
 */
export function hasAttributableRevenue(rows: Array<{ channel: string }>): boolean {
  return rows.some((r) => !isUntrackedChannel(r.channel));
}
